import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Callout, Dialog, EmptyState, Skeleton } from '@bifeai/ui';
import { Delete, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { reloadMe } from '../lib/session';

interface PinUsers {
  deviceName: string;
  users: { id: string; name: string }[];
}

/** Lista de cajeros con PIN en esta terminal (solo responde si la computadora está registrada). */
export function usePinUsers(enabled = true) {
  return useQuery({
    queryKey: ['pin-users'],
    queryFn: () => api.get<PinUsers>('/auth/pin-users'),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Cambio de cajero en la terminal del mostrador: se elige la persona y se escribe su PIN.
 * Sirve también para entrar sin email ni contraseña en la computadora de la caja.
 */
export function PinSwitchDialog({ open, onClose, onSwitched }: { open: boolean; onClose: () => void; onSwitched: () => void }) {
  const qc = useQueryClient();
  const users = usePinUsers(open);
  const [userId, setUserId] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setUserId(null);
      setPin('');
      setError(null);
    }
  }, [open]);

  async function submit(value = pin) {
    if (!userId || value.length < 4) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/pin-switch', { userId, pin: value });
      await reloadMe(qc);
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      onSwitched();
    } catch (err) {
      setError(errorMessage(err));
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  const press = (d: string) => {
    if (busy) return;
    const next = (pin + d).slice(0, 6);
    setPin(next);
  };

  // Teclado físico: números, borrar y Enter.
  useEffect(() => {
    if (!open || !userId) return;
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Enter') void submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const selected = users.data?.users.find((u) => u.id === userId);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={selected ? `Hola, ${selected.name.split(' ')[0]}` : 'Cambiar de cajero'}
      description={users.data ? `Terminal: ${users.data.deviceName}` : undefined}
    >
      {users.isPending ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <Skeleton />
          <Skeleton />
        </div>
      ) : users.isError ? (
        <EmptyState
          icon={<UserRound />}
          title="Esta computadora no es una terminal de mostrador"
          text="Registrala desde Sucursales → Terminales de mostrador para usar PIN."
        />
      ) : !selected ? (
        users.data.users.length === 0 ? (
          <EmptyState icon={<UserRound />} title="Nadie tiene PIN todavía" text="Cada persona crea su PIN desde Mi cuenta." />
        ) : (
          <div className="bf-pin-users">
            {users.data.users.map((u) => (
              <button key={u.id} type="button" className="bf-pin-user" onClick={() => setUserId(u.id)}>
                <Avatar name={u.name} />
                <span>{u.name}</span>
              </button>
            ))}
          </div>
        )
      ) : (
        <div className="bf-pinpad-wrap">
          {error && <Callout tone="danger">{error}</Callout>}
          <div className="bf-pin-dots" aria-label={`${pin.length} números escritos`}>
            {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
              <span key={i} data-on={i < pin.length} />
            ))}
          </div>
          <div className="bf-pinpad">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button key={d} type="button" onClick={() => press(d)}>
                {d}
              </button>
            ))}
            <button type="button" onClick={() => setUserId(null)} className="bf-pinpad-aux">
              Volver
            </button>
            <button type="button" onClick={() => press('0')}>
              0
            </button>
            <button type="button" onClick={() => setPin((p) => p.slice(0, -1))} className="bf-pinpad-aux" aria-label="Borrar">
              <Delete />
            </button>
          </div>
          <Button variant="primary" block loading={busy} disabled={pin.length < 4} onClick={() => void submit()}>
            Entrar
          </Button>
        </div>
      )}
    </Dialog>
  );
}
